import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { ManualeButton } from "@/components/ManualeButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Lock, Unlock, CheckCircle2, Wallet, Loader2, ShieldCheck, Building2, FileDown, AlertTriangle, CalendarRange } from "lucide-react";
import { DateInputIT } from "@/components/DateInputIT";
import { fmtIT } from "@/lib/dateIT";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
const periodRange = (tipo, ref) => {
  const d = new Date(`${ref}T00:00:00`);
  if (tipo === "settimana") {
    const dow = (d.getDay() + 6) % 7; // lunedì = 0
    const start = new Date(d); start.setDate(d.getDate() - dow);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return [fmtDate(start), fmtDate(end)];
  }
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return [fmtDate(start), fmtDate(end)];
};
const EMPTY = {
  contraente: "", compagnia: "", ramo: "", numero_polizza: "",
  codice_ufficio: "", variazione: "", data_scadenza: "", data_emissione: "",
  codice_operazione: "QN", sottotipo_carico: "QT", modalita_pagamento: "contante",
  premio_incassato: "", uscita: "", rimborsi: "", provvigioni_attive: "", partite_varie: "",
};

function StatCard({ label, value, accent, danger }) {
  const cls = danger
    ? "border-red-300 bg-red-50 text-red-700"
    : accent
      ? "border-slate-900 bg-slate-900 text-white"
      : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={`fc-stat-${label}`}>
      <div className={`text-[11px] uppercase tracking-wide flex items-center gap-1 ${danger ? "text-red-500" : accent ? "text-slate-300" : "text-slate-400"}`}>
        {danger && <AlertTriangle className="h-3 w-3" />}{label}
      </div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}

export default function FoglioCassa() {
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState("");
  const [produttore, setProduttore] = useState("");
  const [state, setState] = useState({ movimenti: [], riepilogo: {}, chiusa: false, chiusura: null, foglio: null });
  const [dashboard, setDashboard] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sospesoInput, setSospesoInput] = useState("");
  const [periodo, setPeriodo] = useState("mese");
  const [riep, setRiep] = useState(null);

  useEffect(() => {
    api.get("/fc/meta").then(({ data: m }) => {
      setMeta(m); setData(m.oggi);
      setProduttore(m.can_view_all ? m.produttori[0] : (m.my_produttore || ""));
    }).catch(() => toast.error("Errore caricamento"));
  }, []);

  const load = useCallback(async () => {
    if (!data) return;
    try {
      const params = { data };
      if (meta?.can_view_all) params.produttore = produttore;
      const { data: d } = await api.get("/fc/movimenti", { params });
      setState(d);
      setSospesoInput(d.riepilogo?.sospeso_manuale != null ? String(d.riepilogo.sospeso_manuale) : "");
      if (meta?.can_view_all) {
        const { data: dash } = await api.get("/fc/dashboard", { params: { data } });
        setDashboard(dash);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [data, produttore, meta]);

  useEffect(() => { if (meta && data) load(); }, [meta, data, produttore, load]);

  const loadRiep = useCallback(async () => {
    if (!meta?.can_view_all || !data) return;
    const [dal, al] = periodRange(periodo, data);
    try {
      const { data: d } = await api.get("/fc/riepilogo-periodo", { params: { dal, al } });
      setRiep(d);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [meta, data, periodo]);

  useEffect(() => { loadRiep(); }, [loadRiep]);

  const scaricaRiepPdf = async () => {
    const [dal, al] = periodRange(periodo, data);
    try {
      const { data: blob } = await api.get("/fc/riepilogo-periodo/pdf", { params: { dal, al }, responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Errore generazione riepilogo"); }
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setDlg(true); };
  const openEdit = (m) => {
    setForm({
      contraente: m.contraente || "", compagnia: m.compagnia || "", ramo: m.ramo || "",
      numero_polizza: m.numero_polizza || "", codice_ufficio: m.codice_ufficio || "",
      variazione: m.variazione || "", data_scadenza: m.data_scadenza || "", data_emissione: m.data_emissione || "",
      codice_operazione: m.codice_operazione || "QN", sottotipo_carico: m.sottotipo_carico || "QT",
      modalita_pagamento: m.modalita_pagamento || "contante",
      premio_incassato: m.premio_incassato ?? "", uscita: m.uscita ?? "", rimborsi: m.rimborsi ?? "",
      provvigioni_attive: m.provvigioni_attive ?? "", partite_varie: m.partite_varie ?? "",
    });
    setEditId(m.id); setDlg(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, data_fc: data,
        premio_incassato: parseFloat(form.premio_incassato || 0),
        uscita: parseFloat(form.uscita || 0),
        rimborsi: parseFloat(form.rimborsi || 0),
        provvigioni_attive: parseFloat(form.provvigioni_attive || 0),
        partite_varie: parseFloat(form.partite_varie || 0) };
      if (meta?.is_direzione) body.produttore = produttore;
      if (editId) await api.put(`/fc/movimenti/${editId}`, body);
      else await api.post("/fc/movimenti", body);
      toast.success(editId ? "Movimento aggiornato" : "Movimento registrato");
      setDlg(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/fc/movimenti/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const chiudi = async (riapri) => {
    setBusy(true);
    try { await api.post("/fc/chiusura", { data, produttore, riapri }); toast.success(riapri ? "Cassa riaperta" : "Cassa chiusa e validata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const salvaSospeso = async (reset) => {
    setBusy(true);
    try {
      await api.post("/fc/sospeso", { data, produttore, sospeso: reset ? null : sospesoInput });
      toast.success(reset ? "Sospeso ripristinato al calcolato" : "Sospeso di cassa aggiornato");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const validaProvv = async () => {
    setBusy(true);
    try { await api.post("/fc/provvigioni-ok", { data, produttore, ok: true }); toast.success("OK provvigionale registrato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const scaricaReport = async () => {
    try {
      const params = { data };
      if (meta?.can_view_all) params.produttore = produttore;
      const { data: blob } = await api.get("/fc/report", { params, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Errore generazione report"); }
  };

  if (!meta) return <div className="min-h-screen bg-slate-50"><Header /><div className="p-16 text-center text-slate-400">Caricamento…</div></div>;

  const r = state.riepilogo || {};
  const pm = r.per_modalita || {};
  const isDir = meta.is_direzione;
  const canViewAll = meta.can_view_all;
  const canEdit = isDir || (produttore && produttore === meta.my_produttore);
  const canProvv = produttore === meta.produttore_provvigionale;
  const fg = state.foglio || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 text-slate-900"><Wallet className="h-5 w-5" /><h1 className="font-heading text-2xl font-bold">Foglio Cassa</h1></div>
            <p className="text-sm text-slate-500">Nota giornaliera incassi · modello Assieasy{isDir ? " · vista Direzione" : canViewAll ? " · vista Responsabile" : ""}.</p>
            {state.movimenti.length > 0 && (
              <div className="flex items-center gap-2 mt-1" data-testid="fc-foglio-num">
                <Badge variant="outline" className="text-xs">Registro N. {fg.registro_n ?? 1}</Badge>
                <Badge variant="outline" className="text-xs font-mono">Foglio N. {fg.foglio_n ?? "—"}</Badge>
              </div>
            )}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs text-slate-500">Data FC</Label>
              <DateInputIT value={data} onChange={(v) => setData(v)} className="bg-white" testid="fc-date" />
            </div>
            {canViewAll && (
              <div>
                <Label className="text-xs text-slate-500">Produttore</Label>
                <select value={produttore} onChange={(e) => setProduttore(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="fc-produttore">
                  {meta.produttori.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}
            {!canViewAll && <Badge className="bg-slate-900 text-white border-0 h-10 px-3 flex items-center">FC di {produttore || "—"}</Badge>}
            <ManualeButton route="/manuale/foglio-cassa" />
          </div>
        </div>

        {/* Riepilogo */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-3" data-testid="fc-riepilogo">
          <StatCard label="Totale Entrate" value={euro(r.totale_entrate)} />
          <StatCard label="Uscite" value={euro(r.totale_uscite)} />
          <StatCard label="Rimborsi" value={euro(r.totale_rimborsi)} />
          <StatCard label="Saldo di cassa" value={euro(r.saldo_cassa)} accent />
          <StatCard label="Da rimettere (contanti+assegni)" value={euro(r.rimessa)} accent />
          <StatCard label="Sospeso di cassa" value={euro(r.sospeso)} danger />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
          <StatCard label="Contante" value={euro(pm.contante)} />
          <StatCard label="POS" value={euro(pm.pos)} />
          <StatCard label="Assegno" value={euro(pm.assegno)} />
          <StatCard label="Bonifico (Direzione)" value={euro(pm.bonifico)} />
          <StatCard label="Provvigioni" value={euro(r.totale_provvigioni)} />
          <StatCard label="Partite Varie" value={euro(r.partite_varie)} />
          <StatCard label="N. movimenti" value={r.n_movimenti || 0} />
        </div>

        {/* Sospeso manuale (Direzione) */}
        {isDir && (
          <div className="flex flex-wrap items-end gap-2 mb-4 rounded-xl border border-red-200 bg-red-50 p-3" data-testid="fc-sospeso-box">
            <AlertTriangle className="h-4 w-4 text-red-600 mb-2.5" />
            <div>
              <Label className="text-xs text-red-700">Sospeso di cassa — rettifica manuale €</Label>
              <Input type="number" step="0.01" value={sospesoInput} onChange={(e) => setSospesoInput(e.target.value)}
                placeholder={`Calcolato: ${euro(r.sospeso_calcolato)}`} className="bg-white w-52" data-testid="fc-sospeso-input" />
            </div>
            <Button onClick={() => salvaSospeso(false)} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white border-0" data-testid="fc-sospeso-save">Salva sospeso</Button>
            <Button onClick={() => salvaSospeso(true)} disabled={busy} variant="outline" data-testid="fc-sospeso-reset">Ripristina calcolato</Button>
            <span className="text-xs text-red-600 ml-1 mb-2.5">{r.sospeso_manuale != null ? "Valore manuale attivo" : "In uso il valore calcolato"}</span>
          </div>
        )}

        {/* Azioni */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {canEdit && !state.chiusa ? (
            <Button onClick={openNew} className="brand-gradient text-white border-0" data-testid="fc-add"><Plus className="h-4 w-4 mr-1" /> Nuovo movimento</Button>
          ) : state.chiusa ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-0 h-9 px-3 flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Cassa chiusa dalla Direzione</Badge>
          ) : (
            <Badge variant="outline" className="h-9 px-3 flex items-center gap-1 text-slate-500"><Lock className="h-3.5 w-3.5" /> Sola visione</Badge>
          )}
          <Button onClick={scaricaReport} variant="outline" data-testid="fc-report"><FileDown className="h-4 w-4 mr-1" /> Report PDF (Nota Giornaliera)</Button>
          {isDir && (
            <>
              {!state.chiusa ? (
                <Button onClick={() => chiudi(false)} disabled={busy} variant="outline" data-testid="fc-chiudi"><Lock className="h-4 w-4 mr-1" /> Chiudi cassa</Button>
              ) : (
                <Button onClick={() => chiudi(true)} disabled={busy} variant="outline" data-testid="fc-riapri"><Unlock className="h-4 w-4 mr-1" /> Riapri</Button>
              )}
              {canProvv && (
                <Button onClick={validaProvv} disabled={busy} className={`border-0 text-white ${state.chiusura?.provvigioni_ok ? "bg-emerald-600" : "bg-indigo-600 hover:bg-indigo-700"}`} data-testid="fc-provv">
                  <ShieldCheck className="h-4 w-4 mr-1" /> {state.chiusura?.provvigioni_ok ? "Provvigioni validate" : "OK provvigionale (Gianfranco)"}
                </Button>
              )}
            </>
          )}
        </div>

        {/* Tabella movimenti */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Contraente</TableHead><TableHead>Compagnia</TableHead><TableHead>Cod.Uff.</TableHead>
              <TableHead>Polizza</TableHead><TableHead>Var.</TableHead><TableHead>Scadenza</TableHead>
              <TableHead>Cod.Op.</TableHead><TableHead>Pagam.</TableHead>
              <TableHead className="text-right">Entrata</TableHead><TableHead className="text-right">Uscita</TableHead>
              <TableHead className="text-right">Rimborsi</TableHead><TableHead className="text-right">Provv.</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {state.movimenti.length === 0 ? (
                <TableRow><TableCell colSpan={13} className="text-center text-slate-400 py-10">Nessun movimento per questa data.</TableCell></TableRow>
              ) : state.movimenti.map((m) => (
                <TableRow key={m.id} data-testid={`fc-row-${m.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => canEdit && !state.chiusa && openEdit(m)}>
                  <TableCell className="text-sm">{m.contraente || "—"}</TableCell>
                  <TableCell className="text-sm">{m.compagnia || "—"}</TableCell>
                  <TableCell className="text-sm">{m.codice_ufficio || "—"}</TableCell>
                  <TableCell className="text-sm font-mono text-xs">{m.numero_polizza || "—"}</TableCell>
                  <TableCell className="text-sm">{m.variazione || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtIT(m.data_scadenza)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{m.codice_operazione || "—"}</Badge></TableCell>
                  <TableCell className="text-sm">{meta.modalita_label[m.modalita_pagamento]}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{euro(m.premio_incassato)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-500">{euro(m.uscita)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-500">{euro(m.rimborsi)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-500">{euro(m.provvigioni_attive)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {canEdit && !state.chiusa && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(m.id)} data-testid={`fc-del-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Dashboard Direzione */}
        {canViewAll && dashboard && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2 text-slate-700"><Building2 className="h-4 w-4" /><h2 className="font-heading text-lg font-semibold">Cruscotto {isDir ? "Direzione" : "Responsabile"} · {fmtIT(data)}</h2></div>
            <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Produttore</TableHead><TableHead className="text-right">Entrate</TableHead>
                  <TableHead className="text-right">Rimessa</TableHead><TableHead className="text-right">Sospeso</TableHead>
                  <TableHead className="text-right">Movim.</TableHead><TableHead>Stato</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {dashboard.produttori.map((p) => (
                    <TableRow key={p.produttore} className="cursor-pointer hover:bg-slate-50" onClick={() => setProduttore(p.produttore)} data-testid={`fc-dash-${p.produttore}`}>
                      <TableCell className="font-medium text-sm">{p.produttore}</TableCell>
                      <TableCell className="text-right text-sm">{euro(p.riepilogo.totale_entrate)}</TableCell>
                      <TableCell className="text-right text-sm">{euro(p.riepilogo.rimessa)}</TableCell>
                      <TableCell className={`text-right text-sm font-medium ${Number(p.riepilogo.sospeso) !== 0 ? "text-red-600" : "text-slate-400"}`}>{euro(p.riepilogo.sospeso)}</TableCell>
                      <TableCell className="text-right text-sm">{p.riepilogo.n_movimenti}</TableCell>
                      <TableCell>
                        {p.chiusa ? <Badge className="bg-emerald-100 text-emerald-700 border-0"><Lock className="h-3 w-3 mr-1" />Chiusa</Badge> : <Badge variant="outline" className="text-amber-600">Aperta</Badge>}
                        {p.provvigioni_ok && <Badge className="ml-1 bg-indigo-100 text-indigo-700 border-0"><CheckCircle2 className="h-3 w-3 mr-1" />Provv. OK</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Riepilogo periodo per produttore (Responsabili) */}
        {canViewAll && (
          <div className="mt-6" data-testid="fc-riep-periodo">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 text-slate-700">
                <CalendarRange className="h-4 w-4" />
                <h2 className="font-heading text-lg font-semibold">Riepilogo per produttore</h2>
                {riep && <Badge variant="outline" className="text-xs font-mono">{fmtIT(riep.dal)} → {fmtIT(riep.al)}</Badge>}
                {riep && !riep.versamenti_tracciati && <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Versamenti non sincronizzati da Prima Nota</Badge>}
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                  {["settimana", "mese"].map((t) => (
                    <button key={t} onClick={() => setPeriodo(t)} data-testid={`fc-riep-${t}`}
                      className={`px-3 py-1.5 text-sm rounded-md capitalize transition-colors ${periodo === t ? "bg-navy text-white" : "text-slate-600 hover:text-navy"}`}>{t}</button>
                  ))}
                </div>
                <Button onClick={scaricaRiepPdf} variant="outline" size="sm" data-testid="fc-riep-pdf"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Produttore</TableHead><TableHead className="text-right">Fogli</TableHead>
                  <TableHead className="text-right">Movim.</TableHead><TableHead className="text-right">Totale entrate</TableHead>
                  <TableHead className="text-right">Rimessa</TableHead><TableHead className="text-right">Sospeso aperto</TableHead>
                  <TableHead className="text-right">Da versare</TableHead><TableHead className="text-right">Versato banca</TableHead>
                  <TableHead className="text-right">Debito</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {!riep || riep.per_produttore.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Nessun movimento nel periodo.</TableCell></TableRow>
                  ) : (
                    <>
                      {riep.per_produttore.map((p) => (
                        <TableRow key={p.produttore} className="cursor-pointer hover:bg-slate-50" onClick={() => setProduttore(p.produttore)} data-testid={`fc-riep-row-${p.produttore}`}>
                          <TableCell className="font-medium text-sm">{p.produttore}</TableCell>
                          <TableCell className="text-right text-sm">{p.n_fogli}</TableCell>
                          <TableCell className="text-right text-sm">{p.n_movimenti}</TableCell>
                          <TableCell className="text-right text-sm">{euro(p.totale_entrate)}</TableCell>
                          <TableCell className="text-right text-sm">{euro(p.rimessa)}</TableCell>
                          <TableCell className={`text-right text-sm font-medium ${Number(p.sospeso) !== 0 ? "text-red-600" : "text-slate-400"}`}>{euro(p.sospeso)}</TableCell>
                          <TableCell className={`text-right text-sm font-medium ${Number(p.vers_da_versare) > 0 ? "text-amber-600" : "text-slate-400"}`}>{euro(p.vers_da_versare)}</TableCell>
                          <TableCell className={`text-right text-sm ${Number(p.vers_versato) > 0 ? "text-emerald-600" : "text-slate-400"}`}>{euro(p.vers_versato)}</TableCell>
                          <TableCell className={`text-right text-sm font-semibold ${Number(p.debito) > 0 ? "text-red-600" : "text-slate-400"}`}>{euro(p.debito)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-slate-50 font-semibold" data-testid="fc-riep-totale">
                        <TableCell className="text-sm">TOTALE</TableCell>
                        <TableCell className="text-right text-sm">{riep.totale.n_fogli}</TableCell>
                        <TableCell className="text-right text-sm">{riep.totale.n_movimenti}</TableCell>
                        <TableCell className="text-right text-sm">{euro(riep.totale.totale_entrate)}</TableCell>
                        <TableCell className="text-right text-sm">{euro(riep.totale.rimessa)}</TableCell>
                        <TableCell className={`text-right text-sm ${Number(riep.totale.sospeso) !== 0 ? "text-red-600" : "text-slate-400"}`}>{euro(riep.totale.sospeso)}</TableCell>
                        <TableCell className={`text-right text-sm ${Number(riep.totale.vers_da_versare) > 0 ? "text-amber-600" : "text-slate-400"}`}>{euro(riep.totale.vers_da_versare)}</TableCell>
                        <TableCell className={`text-right text-sm ${Number(riep.totale.vers_versato) > 0 ? "text-emerald-600" : "text-slate-400"}`}>{euro(riep.totale.vers_versato)}</TableCell>
                        <TableCell className={`text-right text-sm font-semibold ${Number(riep.totale.debito) > 0 ? "text-red-600" : "text-slate-400"}`}>{euro(riep.totale.debito)}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Dialog nuovo/modifica movimento */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifica movimento" : "Nuovo movimento cassa"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 py-2">
            <div className="space-y-1 col-span-2 md:col-span-1"><Label className="text-xs">Assicurato / Contraente</Label><Input value={form.contraente} onChange={(e) => setForm({ ...form, contraente: e.target.value })} data-testid="fc-f-contraente" /></div>
            <div className="space-y-1"><Label className="text-xs">Compagnia</Label><Input value={form.compagnia} onChange={(e) => setForm({ ...form, compagnia: e.target.value })} data-testid="fc-f-compagnia" /></div>
            <div className="space-y-1"><Label className="text-xs">Ramo</Label><Input value={form.ramo} onChange={(e) => setForm({ ...form, ramo: e.target.value })} data-testid="fc-f-ramo" /></div>
            <div className="space-y-1"><Label className="text-xs">N. polizza</Label><Input value={form.numero_polizza} onChange={(e) => setForm({ ...form, numero_polizza: e.target.value })} data-testid="fc-f-polizza" /></div>
            <div className="space-y-1"><Label className="text-xs">Codice ufficio</Label><Input value={form.codice_ufficio} onChange={(e) => setForm({ ...form, codice_ufficio: e.target.value })} data-testid="fc-f-ufficio" /></div>
            <div className="space-y-1"><Label className="text-xs">Variazione</Label><Input value={form.variazione} onChange={(e) => setForm({ ...form, variazione: e.target.value })} data-testid="fc-f-variazione" /></div>
            <div className="space-y-1"><Label className="text-xs">Data scadenza</Label><DateInputIT value={form.data_scadenza} onChange={(v) => setForm({ ...form, data_scadenza: v })} testid="fc-f-scadenza" /></div>
            <div className="space-y-1"><Label className="text-xs">Data emissione</Label><DateInputIT value={form.data_emissione} onChange={(v) => setForm({ ...form, data_emissione: v })} testid="fc-f-emissione" /></div>
            <div className="space-y-1"><Label className="text-xs">Codice operazione</Label>
              <select value={form.codice_operazione} onChange={(e) => setForm({ ...form, codice_operazione: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="fc-f-codop">
                {meta.codici_operazione.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Sottotipo carico</Label>
              <select value={form.sottotipo_carico} onChange={(e) => setForm({ ...form, sottotipo_carico: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="fc-f-carico">
                {meta.sottotipi.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Modalità pagamento</Label>
              <select value={form.modalita_pagamento} onChange={(e) => setForm({ ...form, modalita_pagamento: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="fc-f-modalita">
                {meta.modalita.map((mo) => <option key={mo} value={mo}>{meta.modalita_label[mo]}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Entrata (premio) €</Label><Input type="number" step="0.01" value={form.premio_incassato} onChange={(e) => setForm({ ...form, premio_incassato: e.target.value })} data-testid="fc-f-premio" /></div>
            <div className="space-y-1"><Label className="text-xs">Uscita €</Label><Input type="number" step="0.01" value={form.uscita} onChange={(e) => setForm({ ...form, uscita: e.target.value })} data-testid="fc-f-uscita" /></div>
            <div className="space-y-1"><Label className="text-xs">Rimborsi €</Label><Input type="number" step="0.01" value={form.rimborsi} onChange={(e) => setForm({ ...form, rimborsi: e.target.value })} data-testid="fc-f-rimborsi" /></div>
            <div className="space-y-1"><Label className="text-xs">Provvigioni €</Label><Input type="number" step="0.01" value={form.provvigioni_attive} onChange={(e) => setForm({ ...form, provvigioni_attive: e.target.value })} data-testid="fc-f-provv" /></div>
            <div className="space-y-1"><Label className="text-xs">Partite varie €</Label><Input type="number" step="0.01" value={form.partite_varie} onChange={(e) => setForm({ ...form, partite_varie: e.target.value })} data-testid="fc-f-varie" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="brand-gradient text-white border-0" data-testid="fc-f-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Aggiorna" : "Salva movimento")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
